import clickUrl from "../assets/sounds/click.ogg?url";
import swapUrl from "../assets/sounds/swap.ogg?url";
import explodeUrl from "../assets/sounds/explode.ogg?url";
import type { SoundEffect } from "../game/engine";

const SOUND_URLS: Record<SoundEffect, string> = {
  click: clickUrl,
  swap: swapUrl,
  explode: explodeUrl,
};

/**
 * Sound effects, decoded once with Web Audio. Browsers only allow audio after a user gesture, so
 * `unlock` must be called from an input handler.
 */
export class SoundPlayer {
  enabled = true;

  private context: AudioContext | null = null;
  /** Every effect passes through this node, so volume changes apply to all sounds at once. */
  private masterGain: GainNode | null = null;
  private volumeLevel = 1;
  private readonly buffers = new Map<SoundEffect, AudioBuffer>();

  /** Playback volume from 0 (silent) to 1 (full). */
  get volume(): number {
    return this.volumeLevel;
  }

  set volume(value: number) {
    this.volumeLevel = Math.min(1, Math.max(0, value));
    if (this.masterGain) this.masterGain.gain.value = this.volumeLevel;
  }

  unlock(): void {
    if (!this.context) {
      try {
        this.context = new AudioContext();
      } catch {
        return;
      }
      this.masterGain = this.context.createGain();
      this.masterGain.gain.value = this.volumeLevel;
      this.masterGain.connect(this.context.destination);
      void this.loadAll();
    }
    if (this.context.state === "suspended") {
      void this.context.resume();
    }
  }

  play(effect: SoundEffect): void {
    if (!this.enabled || !this.context || !this.masterGain) return;
    const buffer = this.buffers.get(effect);
    if (!buffer) return;

    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.masterGain);
    source.start();
  }

  private async loadAll(): Promise<void> {
    const context = this.context;
    if (!context) return;

    await Promise.all(
      (Object.keys(SOUND_URLS) as SoundEffect[]).map(async (effect) => {
        try {
          const response = await fetch(SOUND_URLS[effect]);
          const data = await response.arrayBuffer();
          this.buffers.set(effect, await context.decodeAudioData(data));
        } catch (error) {
          console.warn(`Could not load sound "${effect}"`, error);
        }
      }),
    );
  }
}
